import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Filter, AlertCircle } from 'lucide-react';
import { reportService } from '@/services/reportService';
import { useToast } from '@/hooks/use-toast';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const TrendsAnalysisReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hospitalId, setHospitalId] = useState('all');
  const { toast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (hospitalId && hospitalId !== 'all') filters.hospital_id = parseInt(hospitalId);

      const response = await reportService.getTrendsAnalysis(period, filters);
      setData(response);
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast({ title: 'Eroare', description: 'Nu s-au putut încărca datele tendințelor', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Filter className="h-5 w-5" />Filtre raport</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="period">Perioadă</Label>
              <Select value={period} onValueChange={(value: any) => setPeriod(value)}>
                <SelectTrigger id="period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Lunar</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="start-date">Data început</Label>
              <input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-date">Data sfârșit</Label>
              <input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="hospital">Spital</Label>
              <Select value={hospitalId} onValueChange={setHospitalId}>
                <SelectTrigger id="hospital"><SelectValue placeholder="Toate spitalele" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate spitalele</SelectItem>
                  <SelectItem value="1">Spital 1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchData} className="w-full"><Filter className="h-4 w-4 mr-2" />Aplică filtre</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="flex justify-center py-8"><LoadingSpinner /></div>}

      {!loading && data && (
        <>
          <Card>
            <CardHeader><CardTitle>Tendințe volume pacienți</CardTitle></CardHeader>
            <CardContent>
              {data.patient_admissions && data.patient_admissions.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.patient_admissions}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total_patients" stroke="#3b82f6" name="Total pacienți" />
                    <Line type="monotone" dataKey="premature_patients" stroke="#10b981" name="Prematuri" />
                    <Line type="monotone" dataKey="chd_patients" stroke="#ef4444" name="MCC" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Alert><AlertCircle className="h-4 w-4" /><AlertDescription>Nu există date disponibile</AlertDescription></Alert>
              )}
            </CardContent>
          </Card>

          {data.year_over_year && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <TrendingUp className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                    <p className="text-3xl font-bold text-blue-600">{data.year_over_year.current_year_total}</p>
                    <p className="text-sm text-muted-foreground">An curent</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <TrendingUp className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                    <p className="text-3xl font-bold text-gray-600">{data.year_over_year.previous_year_total}</p>
                    <p className="text-sm text-muted-foreground">An precedent</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-600" />
                    <p className="text-3xl font-bold text-green-600">{data.year_over_year.growth_rate?.toFixed(1)}%</p>
                    <p className="text-sm text-muted-foreground">Creștere</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};
